import type { SupportCategory, SupportPriority, SupportStatus } from "./types";

/**
 * The operator console's words for a ticket, which are not the customer's.
 *
 * The dashboard says "Waiting on us" and "Waiting on you" because it is talking
 * to the person who filed it. Here the same two states are the queue and the
 * ball being out of court, and an operator scanning a hundred rows wants the
 * shorter word.
 */
export const STATUS_LABELS: Record<SupportStatus, string> = {
  open: "Open",
  pending: "Awaiting customer",
  resolved: "Resolved",
  closed: "Closed",
};

export const STATUS_TONE: Record<SupportStatus, { styles: string; dot: string }> = {
  open: { styles: "border-accent/30 bg-accent-soft/50 text-accent-hover", dot: "bg-accent" },
  pending: { styles: "border-warn/25 bg-warn/8 text-warn", dot: "bg-warn" },
  resolved: { styles: "border-mint/25 bg-mint/8 text-mint", dot: "bg-mint" },
  closed: { styles: "border-line bg-raised text-fg-muted", dot: "bg-fg-subtle" },
};

export const CATEGORY_LABELS: Record<SupportCategory, string> = {
  technical: "Technical",
  billing: "Billing",
  account: "Account",
  abuse: "Abuse",
  other: "Other",
};

export const PRIORITY_LABELS: Record<SupportPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

/**
 * How a priority is painted in a list.
 *
 * Only the two that mean "somebody is stuck" get colour. Painting all four
 * spends the console's warn signal on a field the customer sets themselves,
 * and an operator would stop reading it within a week.
 */
export const PRIORITY_TONE: Record<SupportPriority, "neutral" | "warn" | "bad"> = {
  low: "neutral",
  normal: "neutral",
  high: "warn",
  urgent: "bad",
};

/*
 * The upload rules, mirrored from the API's `services/support.ts`. Duplicated
 * so a file too big is refused before it is sent rather than after; the API
 * remains the authority.
 */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

const ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "text/plain",
  "text/csv",
  "application/json",
  "application/pdf",
  "application/zip",
  "application/gzip",
];

export const ATTACHMENT_ACCEPT = [...ATTACHMENT_TYPES, ".log", ".txt", ".json", ".csv"].join(",");

export function isPreviewable(contentType: string): boolean {
  return ["image/png", "image/jpeg", "image/gif", "image/webp"].includes(contentType);
}

/** A `.log` often arrives with no type at all; only text extensions are widened. */
export function effectiveType(file: File): string {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  return /\.(log|txt)$/i.test(file.name) ? "text/plain" : file.type;
}

export function withEffectiveType(file: File): File {
  const type = effectiveType(file);
  return type === file.type ? file : new File([file], file.name, { type });
}

/** Why a file cannot be attached, in a sentence, or null. */
export function attachmentProblem(file: File, alreadyPicked: number): string | null {
  if (alreadyPicked >= MAX_ATTACHMENTS_PER_MESSAGE) {
    return `Up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > ATTACHMENT_MAX_BYTES) return `${file.name} is over 10 MB.`;
  if (!ATTACHMENT_TYPES.includes(effectiveType(file))) {
    return `${file.name} is not an accepted type.`;
  }
  return null;
}

/**
 * How long the customer has been waiting, in the console's shorthand.
 *
 * Only meaningful while a ticket is open — once we have answered, the elapsed
 * time is the customer's to spend. Returns null for every other state so a
 * column can simply not render.
 */
export function waitingFor(ticket: {
  status: SupportStatus;
  lastMessageAt: string;
}): string | null {
  if (ticket.status !== "open") return null;

  const ms = Date.now() - new Date(ticket.lastMessageAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Whether a wait has gone on long enough to say so in colour.
 *
 * Four hours, and it is a display threshold rather than a commitment: nothing
 * the platform sells promises an answer in any particular time. It exists so a
 * queue an operator has been away from shows them where to start.
 */
export function isStale(ticket: { status: SupportStatus; lastMessageAt: string }): boolean {
  if (ticket.status !== "open") return false;
  return Date.now() - new Date(ticket.lastMessageAt).getTime() > 4 * 60 * 60 * 1000;
}
